import jsPDF from "jspdf";
import "jspdf-autotable";
import {
    IColumn,
    IRow,
} from "../../components/table/collapse/CollapsibleTable";
import DownloadTable, { jsPDFWithPlugin } from "./downloadTable";

export type PdfFragment = {
    text: string;
    color?: string;
    fontStyle?: "bold" | "normal";
};

const getFragmentKey = (key: string) => `${key}__frags`;

const hexToRGB = (hex?: string) => {
    if (!hex) return null;
    let cleanedHex = hex.replace("#", "");
    if (cleanedHex.length === 3) {
        cleanedHex = cleanedHex
            .split("")
            .map((c) => c + c)
            .join("");
    }
    if (cleanedHex.length !== 6) return null;
    const r = parseInt(cleanedHex.substring(0, 2), 16);
    const g = parseInt(cleanedHex.substring(2, 4), 16);
    const b = parseInt(cleanedHex.substring(4, 6), 16);
    return [r, g, b];
};

export type RowData = { [key: string]: any };
class DownloadCollapsibleTable<T extends Object> extends DownloadTable {
    private rows: IRow<T>[];
    private columns: IColumn<T>[];
    private subColumns: IColumn<T>[];

    constructor(
        title: string,
        rows: IRow<T>[],
        columns: IColumn<T>[],
        subColumns: IColumn<T>[],
        filters?: string[]
    ) {
        super(title, filters || []);
        this.rows = rows;
        this.columns = columns;
        this.subColumns = subColumns;
    }

    async GetPDF(userLabel: string, dateLabel: string) {
        const pdf = new jsPDF() as jsPDFWithPlugin;

        this.addMainTable(pdf);

        this.rows.forEach((row) => {
            this.addSubTable(pdf, row);
        });

        this.addFooter(pdf, userLabel, dateLabel);

        pdf.save(`${this.title.toLowerCase().replaceAll(" ", "_")}.pdf`);
    }

    private addMainTable(pdf: jsPDFWithPlugin) {
        pdf.setFontSize(this.TITLE_FONT_SIZE);
        pdf.setTextColor(this.TITLE_COLOR);

        this.addHeader(pdf);
        this.addFilters(pdf);

        const cols = this.columns.map((c) => ({
            header: c.headerName,
            dataKey: c.field,
        }));

        const body = this.rows.map((row) => {
            const flat: RowData = {};
            this.columns.forEach((col) => {
                const value = (row.data as any)[col.field];
                const frags = col.pdfRender?.({ row, value });
                if (frags?.length) {
                    flat[col.field] = "";
                    flat[getFragmentKey(col.field)] = frags;
                } else {
                    flat[col.field] = value;
                }
            });
            return flat;
        });

        pdf.autoTable({
            columns: cols,
            body,
            startY: this.TABLE_START_Y,
            margin: { bottom: this.TABLE_BOTTOM_MARGIN },
            styles: { valign: "middle" },
            rowPageBreak: "avoid",
            didParseCell: (data) => {
                const frags = (data.row.raw as RowData)[
                    getFragmentKey(String(data.column.dataKey))
                ] as PdfFragment[] | undefined;
                if (!frags?.length) return;

                (data.cell as any)._frags = frags;
                data.cell.text = [""];

                const PAD_L = 4;
                const PAD_R = 4;
                const PAD_T = 0;
                const PAD_B = 0;

                data.cell.styles.cellPadding = {
                    top: PAD_T,
                    bottom: PAD_B,
                    left: PAD_L,
                    right: PAD_R,
                };

                const fs = data.cell.styles.fontSize || 10;
                const lineFactor = 0.6;
                const lh = fs * lineFactor;

                let availW = (data.cell.width ?? 0) - PAD_L - PAD_R;
                if (!Number.isFinite(availW) || availW <= 0)
                    availW = Math.max(20, data.cell.width || 20);

                const doc = data.doc as jsPDF;

                let totalLines = 0;
                for (const f of frags) {
                    const text = f.text || "";
                    const lines = doc.splitTextToSize(text, availW);
                    totalLines += Math.max(1, lines.length);
                }

                const contentH = totalLines * lh;
                data.cell.styles.minCellHeight = Math.max(
                    data.cell.styles.minCellHeight || 0,
                    contentH + PAD_T + PAD_B
                );
            },
            didDrawCell: (data) => {
                const frags = (data.cell as any)._frags as
                    | PdfFragment[]
                    | undefined;
                if (!frags?.length) return;

                const doc = data.doc as jsPDF;
                const fs = data.cell.styles.fontSize || 10;
                const lineFactor = 0.6;
                const lh = fs * lineFactor;

                const PAD_L = 4;
                const PAD_R = 4;

                const x = data.cell.x + PAD_L;
                const availW = (data.cell.width ?? 0) - PAD_L - PAD_R;

                let totalLines = 0;
                for (const f of frags) {
                    const lines = doc.splitTextToSize(
                        f.text || "",
                        Math.max(20, availW)
                    );
                    totalLines += Math.max(1, lines.length);
                }
                const contentH = totalLines * lh;
                const yTop = data.cell.y + (data.cell.height - contentH) / 2;

                let cursor = 0;
                for (const f of frags) {
                    const rgb = hexToRGB(f.color);
                    if (f.fontStyle === "bold")
                        doc.setFont(undefined as any, "bold");
                    if (rgb) doc.setTextColor(rgb[0], rgb[1], rgb[2]);

                    const lines = doc.splitTextToSize(
                        f.text || "",
                        Math.max(20, availW)
                    );

                    lines.forEach((ln: string, i: number) =>
                        doc.text(ln, x, yTop + (cursor + i + 0.5) * lh, {
                            baseline: "middle",
                        })
                    );
                    cursor += Math.max(1, lines.length);

                    doc.setFont(undefined as any, "normal");
                    doc.setTextColor(0, 0, 0);
                }
            },
        });
    }

    private addSubTable(pdf: jsPDFWithPlugin, row: IRow<T>) {
        pdf.addPage();

        const subtitle = `${(row.data as any)[this.columns[0].field]}`;
        const pageWidth = pdf.internal.pageSize.getWidth();
        pdf.setFontSize(this.TITLE_FONT_SIZE);
        pdf.setTextColor(this.TITLE_COLOR);
        const subtitleStartX =
            (pageWidth -
                (pdf.getStringUnitWidth(subtitle) * pdf.getFontSize()) /
                    pdf.internal.scaleFactor) /
            2;
        pdf.text(subtitle, subtitleStartX, this.TEXT_START_Y);

        this.addFilters(pdf);

        const cols = this.subColumns.map((c) => ({
            header: c.headerName,
            dataKey: c.field,
        }));
        const body = row.subRowData.map((sub) => {
            const flat: RowData = {};
            this.subColumns.forEach((col) => {
                const value = (sub as any)[col.field];
                const frags = col.pdfRender?.({ row, subrow: sub, value });
                if (frags?.length) {
                    flat[col.field] = "";
                    flat[getFragmentKey(col.field)] = frags;
                } else {
                    flat[col.field] = value;
                }
            });
            return flat;
        });

        pdf.autoTable({
            columns: cols,
            body,
            startY: this.TABLE_START_Y,
            margin: { bottom: this.TABLE_BOTTOM_MARGIN },
            styles: { valign: "middle" },
            columnStyles: {
                date: { minCellWidth: 40 },
            },
            rowPageBreak: "avoid",
            didParseCell: (data) => {
                const frags = (data.row.raw as RowData)[
                    getFragmentKey(String(data.column.dataKey))
                ] as PdfFragment[] | undefined;
                if (!frags?.length) return;

                (data.cell as any)._frags = frags;
                data.cell.text = [""];

                const PAD_L = 4;
                const PAD_R = 4;
                const PAD_T = 0;
                const PAD_B = 0;

                data.cell.styles.cellPadding = {
                    top: PAD_T,
                    bottom: PAD_B,
                    left: PAD_L,
                    right: PAD_R,
                };

                const fs = data.cell.styles.fontSize || 10;
                const lineFactor = 0.5;
                const lh = fs * lineFactor;

                let availW = (data.cell.width ?? 0) - PAD_L - PAD_R;
                if (!Number.isFinite(availW) || availW <= 0)
                    availW = Math.max(20, data.cell.width || 20);

                const doc = data.doc as jsPDF;

                let totalLines = 0;
                for (const f of frags) {
                    const text = f.text || "";
                    const lines = doc.splitTextToSize(text, availW);
                    totalLines += Math.max(1, lines.length);
                }

                const contentH = totalLines * lh;
                data.cell.styles.minCellHeight = Math.max(
                    data.cell.styles.minCellHeight || 0,
                    contentH + PAD_T + PAD_B
                );
            },
            didDrawCell: (data) => {
                const frags = (data.cell as any)._frags as
                    | PdfFragment[]
                    | undefined;
                if (!frags?.length) return;

                const doc = data.doc as jsPDF;
                const fs = data.cell.styles.fontSize || 10;
                const lineFactor = 0.5;
                const lh = fs * lineFactor;

                const PAD_L = 4;
                const PAD_R = 4;

                const x = data.cell.x + PAD_L;
                const availW = (data.cell.width ?? 0) - PAD_L - PAD_R;

                let totalLines = 0;
                for (const f of frags) {
                    const lines = doc.splitTextToSize(
                        f.text || "",
                        Math.max(20, availW)
                    );
                    totalLines += Math.max(1, lines.length);
                }
                const contentH = totalLines * lh;
                const yTop = data.cell.y + (data.cell.height - contentH) / 2;
                let cursor = 0;
                for (const f of frags) {
                    const rgb = hexToRGB(f.color);
                    const defaultColor = data.cell.styles.textColor;

                    doc.setFont(
                        undefined as any,
                        f.fontStyle === "bold" ? "bold" : "normal"
                    );

                    if (rgb) {
                        doc.setTextColor(rgb[0], rgb[1], rgb[2]);
                    } else if (Array.isArray(defaultColor)) {
                        doc.setTextColor(
                            defaultColor[0],
                            defaultColor[1],
                            defaultColor[2]
                        );
                    } else if (typeof defaultColor === "number") {
                        doc.setTextColor(defaultColor);
                    } else {
                        doc.setTextColor(0, 0, 0);
                    }

                    const lines = doc.splitTextToSize(
                        f.text || "",
                        Math.max(20, availW)
                    );
                    lines.forEach((ln: string, i: number) =>
                        doc.text(ln, x, yTop + (cursor + i + 0.5) * lh, {
                            baseline: "middle",
                        })
                    );

                    cursor += Math.max(1, lines.length);

                    doc.setFont(undefined as any, "normal");
                    doc.setTextColor(0, 0, 0);
                }
            },
        });
    }
}

export default DownloadCollapsibleTable;
